import { useState } from "react"
import { UUID } from "shared-ts/classes/uuid"
import {
  FilterT,
  FilterType,
} from "shared-ts/hooks/useFilter/types/filter.types"

import { Filter } from "../Filter/Filter"

export interface TableControlFiltersProps {
  filters: FilterT[]
  isInModal?: boolean
}

interface FilterGroupT {
  key: string
  selectedFilterKey: string | null
}

function newGroup(): FilterGroupT {
  return {
    key: UUID.newRandom().toString(),
    selectedFilterKey: null,
  }
}

export function FilterGroup({ filters, isInModal }: TableControlFiltersProps) {
  const [filterGroups, setFilterGroups] = useState<FilterGroupT[]>(() => {
    const defaultGroups: FilterGroupT[] = []
    for (const filter of filters) {
      const isTextSelected =
        filter.type === FilterType.Text && filter.selectedOptions.length > 0
      const isDateSelected =
        filter.type === FilterType.Date && filter.selectedDateRange !== null
      if (isTextSelected || isDateSelected) {
        defaultGroups.push({
          ...newGroup(),
          selectedFilterKey: filter.key,
        })
      }
    }
    if (defaultGroups.length < filters.length) {
      defaultGroups.push(newGroup())
    }
    return defaultGroups
  })

  if (!filters.length) {
    return null
  }

  const selectedFilterOptions: Record<string, boolean> = {}
  for (const fg of filterGroups) {
    if (fg.selectedFilterKey) {
      selectedFilterOptions[fg.selectedFilterKey] = true
    }
  }

  const filterOptions = filters.filter((f) => !selectedFilterOptions[f.key])

  function handleSelectGroupFilterKey(groupKey: string, filterKey: string) {
    setFilterGroups((prevGroups) => {
      const newFilterGroups = [...prevGroups]
      const groupIndex = newFilterGroups.findIndex((fg) => fg.key === groupKey)
      newFilterGroups[groupIndex].selectedFilterKey = filterKey

      if (filterOptions.length > 1) {
        newFilterGroups.push(newGroup())
      }

      return newFilterGroups
    })
  }

  function handleDeleteGroupFilterKey(groupKey: string) {
    const group = filterGroups.find((f) => f.key === groupKey)
    const filter = filters.find((f) => f.key === group?.selectedFilterKey)
    filter?.onClear()

    setFilterGroups((prevGroups) => {
      const newFilterGroups = prevGroups.filter((fg) => fg.key !== groupKey)

      if (filterOptions.length === 0) {
        newFilterGroups.push(newGroup())
      }

      return newFilterGroups
    })
  }

  return filterGroups.map((fg) => {
    return (
      <Filter
        key={fg.key}
        filters={filterOptions}
        selectedFilter={
          filters.find((f) => f.key === fg.selectedFilterKey) ?? null
        }
        onSelectFilterKey={(filterKey) =>
          handleSelectGroupFilterKey(fg.key, filterKey)
        }
        onDeleteFilter={() => handleDeleteGroupFilterKey(fg.key)}
        isInModal={isInModal}
      />
    )
  })
}
